import Modal from "common/Modal/Modal";
import { BotType, LanguageNameSpaces } from "@shared/types";
import { BOT_TYPE_TO_DISPLAY_NAME } from "@shared/features/bots/constants";
import styled, { useTheme } from "styled-components";
import Flex from "common/Boxes/Flex";
import Title from "common/Typography/Title";
import Text from "common/Typography/Text";
import {
  BOT_TYPE_TO_ARTICLE_DATA,
  BOT_TYPE_TO_DESCRIPTION,
  BOT_TYPE_TO_MAIN_ARTICLE_ID,
  BOT_TYPE_TO_YOUTUBE_EMBED_LINK,
  NEW_BOT_ID_QUERY_PARAM,
} from "features/bots/common/constants";
import React, { useEffect, useState } from "react";
import ExportOutlined from "@ant-design/icons/ExportOutlined";
import PlayCircleOutlined from "@ant-design/icons/PlayCircleOutlined";
import Link from "common/Typography/Link";
import Col from "antd/es/col";
import Row from "antd/es/row";
import IntercomLink from "common/Text/IntercomLink";
import YoutubeVideoModal from "common/Video/YoutubeVideoModal";
import { localStorageService } from "utils/localStorageService";
import useMedia from "utils/hooks/useMedia";
import { SMALL_SCREEN } from "common/constants";
import LottieAnimation from "common/Animations/LottieAnimation.tsx";
import signalsBotAnimation from "features/bots/BotsIntroduction/signals_bot.json";
import dcaBotAnimation from "features/bots/BotsIntroduction/dca_bot.json";
import gridBotAnimation from "features/bots/BotsIntroduction/grid_bot.json";
import tradingViewBotAnimation from "features/bots/BotsIntroduction/trading_view_bot.json";
import {
  DarkDcaBotIcon,
  DarkGridBotIcon,
  DarkHedgingBotIcon,
  DarkSignalsBotIcon,
  DarkTradingViewBotIcon,
  DcaBotIcon,
  HedgingBotIcon,
  GridBotIcon,
  SignalsBotIcon,
  TradingViewBotIcon,
} from "common/Icons/Icons.tsx";
import useQuery from "utils/hooks/useQuery.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { TranslationKeys } from "@shared/common/i18n/types";
import { joinWords } from "@shared/common/i18n/utils";
import { useBranding } from "@shared/utils/hooks/useBranding.ts";
import NewLookBotsIntroduction from "features/bots/NewLook/NewLookBotsIntroduction";

interface BotsIntroductionModalProps {
  botType: BotType;
}

const BotsIntroductionModal = ({ botType }: BotsIntroductionModalProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const theme = useTheme();
  const query = useQuery();
  const isDarkMode = theme.isDarkMode;
  const isSmallScreen = useMedia(
    [`(max-width: ${SMALL_SCREEN})`],
    [true],
    false
  );
  const [dismissedBotTypes, setDismissedBotTypes] = useState(
    () =>
      localStorageService.getParsedValue<Partial<Record<BotType, number>>>(
        LOCAL_STORAGE_KEY
      ) || {}
  );
  const { isWhitelabel } = useBranding();
  const hideModal = !!query.get(NEW_BOT_ID_QUERY_PARAM);
  const shouldOpen =
    botType !== BotType.TRADING_VIEW_SIGNALS &&
    botType !== BotType.HEDGING &&
    !dismissedBotTypes?.[botType] &&
    !hideModal &&
    !isWhitelabel;
  const [open, setOpen] = useState(shouldOpen);
  const [youtubeModalOpen, setYoutubeModalOpen] = useState(false);
  const botTypeDisplayName = joinWords([
    t(BOT_TYPE_TO_DISPLAY_NAME[botType]),
    t("bots"),
  ]);

  useEffect(() => {
    if (!open && shouldOpen) {
      setOpen(true);
    }
  }, [open, shouldOpen]);

  const onCloseClick = () => {
    const dismissed = { ...dismissedBotTypes, [botType]: 1 };
    setDismissedBotTypes(dismissed);
    setOpen(false);
    try {
      localStorageService.setValue(
        LOCAL_STORAGE_KEY,
        JSON.stringify(dismissed)
      );
    } catch {}
  };

  const botTypeToIcon = {
    [BotType.SIGNAL]: isDarkMode ? DarkSignalsBotIcon : SignalsBotIcon,
    [BotType.ASAP]: isDarkMode ? DarkDcaBotIcon : DcaBotIcon,
    [BotType.GRID]: isDarkMode ? DarkGridBotIcon : GridBotIcon,
    [BotType.TRADING_VIEW]: isDarkMode
      ? DarkTradingViewBotIcon
      : TradingViewBotIcon,
    [BotType.HEDGING]: isDarkMode ? DarkHedgingBotIcon : HedgingBotIcon,
  };
  const Icon = botTypeToIcon[botType];

  const botTypeToAnimationData = {
    [BotType.SIGNAL]: signalsBotAnimation,
    [BotType.ASAP]: dcaBotAnimation,
    [BotType.GRID]: gridBotAnimation,
    [BotType.TRADING_VIEW]: tradingViewBotAnimation,
    [BotType.HEDGING]: dcaBotAnimation,
  };

  const articles = (
    <Row gutter={isSmallScreen ? 20 : 32} style={{ marginTop: 16 }}>
      {BOT_TYPE_TO_ARTICLE_DATA[botType]?.map((articleData, index) => (
        <Col span={8} key={index}>
          <Border />
          <Title level={4}>{t(articleData.title)}</Title>
          <Text type="secondary">{t(articleData.description)}</Text>
          <IntercomLink
            articleId={articleData.id}
            style={{ fontStyle: "italic" }}
          >
            {t("buttons.read_more")}
          </IntercomLink>
        </Col>
      ))}
    </Row>
  );

  const getContent = () => {
    if (botType === BotType.TRADING_VIEW_SIGNALS) return;
    if (theme.isNewLook) {
      return (
        <NewLookBotsIntroduction
          botType={botType}
          title={`${t("modals.welcome")} ${botTypeDisplayName}!`}
          animationData={botTypeToAnimationData[botType]}
          onWatchVideo={() => setYoutubeModalOpen(true)}
        />
      );
    }
    return (
      <>
        <TopSection>
          <MainParagraph>
            <MainTitleWrapper>
              <Icon style={{ width: 50, height: 50 }} />
              <MainTitle>{botTypeDisplayName}</MainTitle>
            </MainTitleWrapper>
            <Text>
              {t(
                BOT_TYPE_TO_DESCRIPTION[
                  botType
                ] as TranslationKeys<LanguageNameSpaces.BOTS>
              )}
            </Text>
            <Actions>
              <Link onClick={() => setYoutubeModalOpen(true)}>
                <PlayCircleOutlined style={{ marginRight: 6 }} />
                {t("modals.watch_video")}
              </Link>
              <Link as={"div"}>
                <ExportOutlined style={{ marginRight: 6 }} />
                <IntercomLink
                  articleId={BOT_TYPE_TO_MAIN_ARTICLE_ID[botType]}
                />
              </Link>
            </Actions>
          </MainParagraph>
          <LottieAnimation animationData={botTypeToAnimationData[botType]} />
        </TopSection>
        <Title level={3}>{t("modals.everything_you_need")}</Title>
        {articles}
      </>
    );
  };

  return (
    <>
      <Modal
        open={open}
        onCancel={onCloseClick}
        title={
          theme.isNewLook
            ? undefined
            : `${joinWords([t("modals.welcome"), botTypeDisplayName])}!`
        }
        width={theme.isNewLook ? 880 : 900}
        centered={true}
      >
        {getContent()}
      </Modal>
      <YoutubeVideoModal
        open={youtubeModalOpen}
        closeModal={() => setYoutubeModalOpen(false)}
        embedLink={BOT_TYPE_TO_YOUTUBE_EMBED_LINK[botType]}
      />
    </>
  );
};

export default BotsIntroductionModal;

const LOCAL_STORAGE_KEY = "bots_modal";

const TopSection = styled(Flex)`
  border-bottom: ${(p) => p.theme.borders.light};
  padding-bottom: 24px;
  margin-bottom: 24px;
  align-items: flex-start;
  column-gap: 16px;
`;
const MainParagraph = styled(Flex)`
  flex-direction: column;
  align-items: flex-start;
  max-width: 350px;
  height: 100%;
`;
const MainTitleWrapper = styled(Flex)`
  margin-bottom: 16px;
  justify-content: flex-start;
`;
const MainTitle = styled(Title).attrs({ level: 2 })`
  font-weight: ${(p) => p.theme.typography.fontWeights.BOLD};
  margin-left: 12px;
`;
const Actions = styled(Flex)`
  margin-top: 36px;
  column-gap: 24px;
`;
const Border = styled.div`
  width: 100%;
  height: 7px;
  border-radius: 6px 6px 0 0;
  background: ${(p) => p.theme.colors.midBlue};
  margin-bottom: 12px;
`;
